<!-- Style modal — single shell drives both Add Style and Edit Style. Mode
     swaps the header copy, the save-button label, and whether the Rules +
     Actions sections appear (they need a styleId, so add-mode never shows
     them; edit-mode hides them only for the catch-all "default" style). -->
<div
  *ngIf="open && form() as sf"
  class="fixed inset-0 flex items-center justify-center bg-main-background bg-opacity-75 z-40 p-4">
  <form
    (ngSubmit)="onSubmit()"
    class="bg-white/95 shadow-xl border border-gray-200 rounded-md w-full max-w-[900px] max-h-[90vh] flex flex-col overflow-hidden">
    <div class="flex items-center justify-between bg-blue-500 text-white px-5 py-3 rounded-t-md shrink-0">
      <p class="oswald-font text-lg font-medium">
        <ng-container *ngIf="mode === 'add'">Add Style</ng-container>
        <ng-container *ngIf="mode === 'edit'">Edit Style</ng-container>
      </p>
      <button type="button" (click)="onClose()" [disabled]="saving" class="text-white/80 hover:text-white disabled:opacity-60">
        <lucide-icon [img]="icons.X" [size]="18"></lucide-icon>
      </button>
    </div>

    <div class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-5 py-4 flex flex-col gap-4">
      <!-- Top row: name + geo-type toggles -->
      <div class="flex flex-col sm:flex-row sm:items-end gap-3">
        <div class="flex-1">
          <label class="block mb-1 text-xs font-bold text-gray-900">
            Style Name <span class="text-red-500">*</span>
          </label>
          <input
            type="text"
            [ngModel]="sf.name"
            (ngModelChange)="updateField('name', $event)"
            (blur)="markTouched('name')"
            name="styleName"
            placeholder="Style Name"
            [disabled]="saving"
            [class.border-red-400]="showError('name', !sf.name.trim())"
            [class.border-gray-300]="!showError('name', !sf.name.trim())"
            class="bg-gray-50 border text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2 disabled:opacity-60" />
          <p *ngIf="showError('name', !sf.name.trim())" class="text-xs text-red-500 mt-1">
            Name is required.
          </p>
        </div>
        <div class="flex items-center gap-4 text-sm pb-1 sm:pb-2">
          <label class="inline-flex items-center gap-1 cursor-pointer font-bold">
            <input type="checkbox" [checked]="sf.geoPoint" (change)="updateField('geoPoint', $any($event.target).checked)" [disabled]="saving" class="w-4 h-4 accent-blue-500" />
            Point
          </label>
          <label class="inline-flex items-center gap-1 cursor-pointer font-bold">
            <input type="checkbox" [checked]="sf.geoLine" (change)="updateField('geoLine', $any($event.target).checked)" [disabled]="saving" class="w-4 h-4 accent-blue-500" />
            Line
          </label>
          <label class="inline-flex items-center gap-1 cursor-pointer font-bold">
            <input type="checkbox" [checked]="sf.geoPolygon" (change)="updateField('geoPolygon', $any($event.target).checked)" [disabled]="saving" class="w-4 h-4 accent-blue-500" />
            Polygon
          </label>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- Display section -->
        <section class="border border-gray-200 rounded-md overflow-hidden">
          <div class="bg-blue-500 text-white px-3 py-1.5"><b class="oswald-font text-sm">Display</b></div>
          <div class="px-3 py-3 flex flex-col gap-3 text-sm">
            <div class="flex items-center gap-3">
              <b class="w-32 shrink-0">Vertical Position</b>
              <input type="number" [ngModel]="sf.zIndex" (ngModelChange)="updateField('zIndex', $event)" name="styleZIndex" [disabled]="saving" class="bg-gray-50 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 block flex-1 p-2 disabled:opacity-60" />
            </div>
            <div class="flex items-center gap-3">
              <b class="w-32 shrink-0">Min/Max Zoom</b>
              <div class="flex items-center gap-2 flex-1">
                <input type="number" min="1" max="30" [ngModel]="sf.minZoom" (ngModelChange)="updateField('minZoom', $event)" name="styleMinZoom" [disabled]="saving" class="bg-gray-50 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 block w-20 p-2 disabled:opacity-60" />
                <span class="text-gray-400">–</span>
                <input type="number" min="1" max="30" [ngModel]="sf.maxZoom" (ngModelChange)="updateField('maxZoom', $event)" name="styleMaxZoom" [disabled]="saving" class="bg-gray-50 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 block w-20 p-2 disabled:opacity-60" />
              </div>
            </div>
          </div>
        </section>

        <!-- Points section -->
        <section class="border border-gray-200 rounded-md overflow-hidden">
          <div class="bg-blue-500 text-white px-3 py-1.5"><b class="oswald-font text-sm">Points</b></div>
          <div class="px-3 py-3 flex flex-col gap-3 text-sm">
            <div class="flex items-start gap-3">
              <b class="w-32 shrink-0 pt-2">Icon</b>
              <div class="flex-1 flex flex-col gap-2">
                <div class="flex items-center gap-2">
                  <div class="w-10 h-10 shrink-0 border border-gray-300 rounded-md flex items-center justify-center bg-white overflow-hidden">
                    <img *ngIf="sf.icon" [src]="sf.icon" alt="" class="max-w-full max-h-full" />
                    <span *ngIf="!sf.icon" class="text-gray-300 text-xs">—</span>
                  </div>
                  <select [ngModel]="sf.icon" (ngModelChange)="onIconPicked($event)" name="styleIcon" [disabled]="saving || iconsLoading" class="bg-gray-50 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 block flex-1 p-2 disabled:opacity-60">
                    <option [ngValue]="''">— No Icon —</option>
                    <option *ngFor="let i of icons$list" [ngValue]="i.url">{{ i.filename }}</option>
                  </select>
                </div>
                <div class="flex items-center gap-2">
                  <label class="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-xs oswald-font cursor-pointer" [class.opacity-60]="iconUploading || saving" [class.cursor-not-allowed]="iconUploading || saving">
                    <lucide-icon [img]="icons.Plus" [size]="14"></lucide-icon>
                    {{ iconUploading ? 'Uploading…' : 'Upload icon' }}
                    <input type="file" class="hidden" accept=".png,.bmp,.jpg,.jpeg,.gif,.svg,image/png,image/bmp,image/jpeg,image/gif,image/svg+xml" [disabled]="iconUploading || saving" (change)="onIconFileChange($event)" />
                  </label>
                  <span *ngIf="sf.iconWidth && sf.iconHeight" class="text-xs text-gray-500 tabular-nums">{{ sf.iconWidth }} × {{ sf.iconHeight }} px</span>
                </div>
              </div>
            </div>
            <div class="flex items-center gap-3">
              <b class="w-32 shrink-0">Icon Align</b>
              <select [ngModel]="sf.iconAlign" (ngModelChange)="updateField('iconAlign', $event)" name="styleIconAlign" [disabled]="saving" class="bg-gray-50 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 block flex-1 p-2 disabled:opacity-60">
                <option [ngValue]="'T'">Top</option>
                <option [ngValue]="'R'">Right</option>
                <option [ngValue]="'C'">Center</option>
                <option [ngValue]="'B'">Bottom</option>
                <option [ngValue]="'L'">Left</option>
              </select>
            </div>
          </div>
        </section>

        <!-- Labels section -->
        <section class="border border-gray-200 rounded-md overflow-hidden">
          <div class="bg-blue-500 text-white px-3 py-1.5"><b class="oswald-font text-sm">Labels</b></div>
          <div class="px-3 py-3 flex flex-col gap-3 text-sm">
            <div class="flex items-center gap-3">
              <b class="w-32 shrink-0">Font Size</b>
              <input type="number" min="1" [ngModel]="sf.labelFontSize" (ngModelChange)="updateField('labelFontSize', $event)" name="styleLabelFontSize" [disabled]="saving" class="bg-gray-50 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 block flex-1 p-2 disabled:opacity-60" />
            </div>
            <div class="flex items-center gap-3">
              <b class="w-32 shrink-0">Position</b>
              <select [ngModel]="sf.labelAlign" (ngModelChange)="updateField('labelAlign', $event)" name="styleLabelAlign" [disabled]="saving" class="bg-gray-50 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 block flex-1 p-2 disabled:opacity-60">
                <option [ngValue]="'L'">Left</option>
                <option [ngValue]="'C'">Center</option>
                <option [ngValue]="'R'">Right</option>
              </select>
            </div>
            <div class="flex items-center gap-3">
              <b class="w-32 shrink-0">Min/Max Zoom</b>
              <div class="flex items-center gap-2 flex-1">
                <input type="number" min="1" max="30" [ngModel]="sf.labelMinZoom" (ngModelChange)="updateField('labelMinZoom', $event)" name="styleLabelMinZoom" [disabled]="saving" class="bg-gray-50 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 block w-20 p-2 disabled:opacity-60" />
                <span class="text-gray-400">–</span>
                <input type="number" min="1" max="30" [ngModel]="sf.labelMaxZoom" (ngModelChange)="updateField('labelMaxZoom', $event)" name="styleLabelMaxZoom" [disabled]="saving" class="bg-gray-50 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 block w-20 p-2 disabled:opacity-60" />
              </div>
            </div>
          </div>
        </section>

        <!-- Lines and Polygons section -->
        <section class="border border-gray-200 rounded-md overflow-hidden">
          <div class="bg-blue-500 text-white px-3 py-1.5"><b class="oswald-font text-sm">Lines and Polygons</b></div>
          <div class="px-3 py-3 flex flex-col gap-3 text-sm">
            <div class="flex items-center gap-3">
              <b class="w-32 shrink-0">Fill Color</b>
              <div class="flex items-center gap-2 flex-1">
                <input type="color" [ngModel]="sf.fillColor" (ngModelChange)="updateField('fillColor', $event)" name="styleFillColor" [disabled]="saving" class="w-10 h-10 p-0.5 border border-gray-300 rounded-md cursor-pointer disabled:opacity-60" />
                <input type="text" [ngModel]="sf.fillColor" (ngModelChange)="updateField('fillColor', $event)" name="styleFillColorHex" [disabled]="saving" class="w-20 bg-gray-50 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 block flex-1 p-2 disabled:opacity-60" />
              </div>
            </div>
            <div class="flex items-center gap-3">
              <b class="w-32 shrink-0">Fill Opacity</b>
              <input type="number" step="0.05" min="0" max="1" [ngModel]="sf.fillOpacity" (ngModelChange)="updateField('fillOpacity', $event)" name="styleFillOpacity" [disabled]="saving" class="bg-gray-50 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 block flex-1 p-2 disabled:opacity-60" />
            </div>
            <div class="flex items-center gap-3">
              <b class="w-32 shrink-0">Stroke Color</b>
              <div class="flex items-center gap-2 flex-1">
                <input type="color" [ngModel]="sf.strokeColor" (ngModelChange)="updateField('strokeColor', $event)" name="styleStrokeColor" [disabled]="saving" class="w-10 h-10 p-0.5 border border-gray-300 rounded-md cursor-pointer disabled:opacity-60" />
                <input type="text" [ngModel]="sf.strokeColor" (ngModelChange)="updateField('strokeColor', $event)" name="styleStrokeColorHex" [disabled]="saving" class="w-20 bg-gray-50 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 block flex-1 p-2 disabled:opacity-60" />
              </div>
            </div>
            <div class="flex items-center gap-3">
              <b class="w-32 shrink-0">Stroke Weight</b>
              <input type="number" min="0" [ngModel]="sf.strokeWeight" (ngModelChange)="updateField('strokeWeight', $event)" name="styleStrokeWeight" [disabled]="saving" class="bg-gray-50 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 block flex-1 p-2 disabled:opacity-60" />
            </div>
            <div class="flex items-center gap-3">
              <b class="w-32 shrink-0">Stroke Opacity</b>
              <input type="number" step="0.05" min="0" max="1" [ngModel]="sf.strokeOpacity" (ngModelChange)="updateField('strokeOpacity', $event)" name="styleStrokeOpacity" [disabled]="saving" class="bg-gray-50 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 block flex-1 p-2 disabled:opacity-60" />
            </div>
          </div>
        </section>
      </div>

      <!-- Rules section — edit-mode only, hidden for "default" style -->
      <section *ngIf="showRulesAndActions()" class="border border-gray-200 rounded-md">
        <div class="flex items-center justify-between bg-blue-500 text-white px-3 py-1.5">
          <b class="oswald-font text-sm">Rules</b>
          <button type="button" (click)="startAddRule()" [disabled]="!!newRuleDraft() || saving" class="flex items-center gap-1 text-xs oswald-font text-white/90 hover:text-white disabled:opacity-60">
            <lucide-icon [img]="icons.Plus" [size]="14"></lucide-icon>
            Add Rule
          </button>
        </div>
        <div class="px-3 py-3 text-sm">
          <div *ngIf="rules.length === 0 && !newRuleDraft()" class="text-xs text-gray-500 italic">
            No rules. Click <b>Add Rule</b> to wire a tracker code to this style.
          </div>
          <table *ngIf="rules.length > 0 || newRuleDraft()" class="w-full text-xs">
            <thead>
              <tr class="text-left text-gray-500">
                <th class="px-1 py-1 font-bold">Field</th>
                <th class="px-1 py-1 font-bold">Condition</th>
                <th class="px-1 py-1 font-bold">Value</th>
                <th class="px-1 py-1 w-16"></th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let r of rules" class="border-t border-gray-100">
                <td class="px-1 py-1.5">{{ r.fieldName || '—' }}</td>
                <td class="px-1 py-1.5 text-gray-600">{{ r.fieldCondition || '—' }}</td>
                <td class="px-1 py-1.5 break-all">{{ ruleValueLabel(r) }}</td>
                <td class="px-1 py-1.5 text-right">
                  <button type="button" (click)="deleteRule.emit(r)" [disabled]="saving" title="Delete rule" class="text-red-600 hover:text-red-800 disabled:opacity-40">
                    <lucide-icon [img]="icons.Trash2" [size]="14"></lucide-icon>
                  </button>
                </td>
              </tr>
              <tr *ngIf="newRuleDraft() as d" class="border-t border-gray-200 bg-blue-50/60">
                <td class="px-1 py-1.5">
                  <select [ngModel]="d.fieldName" (ngModelChange)="onRuleFieldNameChange($event)" [ngModelOptions]="{standalone: true}" [disabled]="ruleSaving" class="bg-white border border-gray-300 rounded text-xs p-1 w-full disabled:opacity-60">
                    <option *ngFor="let f of ruleFieldNames" [ngValue]="f">{{ f }}</option>
                  </select>
                </td>
                <td class="px-1 py-1.5">
                  <select [ngModel]="d.fieldCondition" (ngModelChange)="updateRuleDraft('fieldCondition', $event)" [ngModelOptions]="{standalone: true}" [disabled]="ruleSaving || d.fieldName === 'geofence'" class="bg-white border border-gray-300 rounded text-xs p-1 w-full disabled:opacity-60">
                    <option *ngFor="let c of conditionsFor(d.fieldName)" [ngValue]="c">{{ c }}</option>
                  </select>
                </td>
                <td class="px-1 py-1.5">
                  <!-- geofence rules pick a polygon (stored as its feature id);
                       everything else keeps the free-text value -->
                  <ng-container *ngIf="d.fieldName === 'geofence'; else plainRuleValue">
                    <select [ngModel]="d.fieldValue" (ngModelChange)="updateRuleDraft('fieldValue', $event)" [ngModelOptions]="{standalone: true}" [disabled]="ruleSaving || geofencesLoading" class="bg-white border border-gray-300 rounded text-xs p-1 w-full disabled:opacity-60">
                      <option [ngValue]="''" disabled>
                        {{ geofencesLoading ? 'Loading geofences…' : (geofences.length ? '— Select geofence —' : 'No geofences in this building') }}
                      </option>
                      <option *ngFor="let g of geofences" [ngValue]="'' + g.id">
                        {{ g.name || 'Geofence #' + g.id }}{{ g.description ? ' — ' + g.description : '' }}
                      </option>
                    </select>
                  </ng-container>
                  <ng-template #plainRuleValue>
                    <input type="text" placeholder="e.g. 6030" [ngModel]="d.fieldValue" (ngModelChange)="updateRuleDraft('fieldValue', $event)" [ngModelOptions]="{standalone: true}" [disabled]="ruleSaving" class="bg-white border border-gray-300 rounded text-xs p-1 w-full disabled:opacity-60" />
                  </ng-template>
                </td>
                <td class="px-1 py-1.5 text-right">
                  <div class="flex justify-end gap-1">
                    <button type="button" (click)="onSaveRule()" [disabled]="ruleSaving" title="Save rule" class="text-green-700 hover:text-green-800 disabled:opacity-40">
                      <lucide-icon [img]="icons.Check" [size]="14"></lucide-icon>
                    </button>
                    <button type="button" (click)="cancelAddRule()" [disabled]="ruleSaving" title="Cancel" class="text-gray-500 hover:text-gray-700 disabled:opacity-40">
                      <lucide-icon [img]="icons.X" [size]="14"></lucide-icon>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Live map preview of the geofence picked in the draft row
               (v2 shows static-map thumbnails; this is the same idea). -->
          <div *ngIf="draftGeofence() as gf" class="mt-2 border border-gray-200 rounded-md overflow-hidden">
            <div class="bg-gray-50 px-2 py-1 text-[11px] text-gray-600">
              <b>{{ gf.name || 'Geofence #' + gf.id }}</b>
              <ng-container *ngIf="gf.description"> — {{ gf.description }}</ng-container>
            </div>
            <div *ngIf="!previewUnavailable()" #geofencePreview class="w-full h-48"></div>
            <div *ngIf="previewUnavailable()" class="px-2 py-3 text-xs text-gray-500 italic">
              Map preview unavailable for this geofence.
            </div>
          </div>
        </div>
      </section>

      <!-- Actions section — edit-mode only, hidden for "default" style -->
      <section *ngIf="showRulesAndActions()" class="border border-gray-200 rounded-md">
        <div class="flex items-center justify-between bg-blue-500 text-white px-3 py-1.5">
          <b class="oswald-font text-sm">Actions</b>
          <button type="button" (click)="startAddAction()" [disabled]="!!newActionDraft() || saving" class="flex items-center gap-1 text-xs oswald-font text-white/90 hover:text-white disabled:opacity-60">
            <lucide-icon [img]="icons.Plus" [size]="14"></lucide-icon>
            Add Action
          </button>
        </div>
        <div class="px-3 py-3 text-sm">
          <div *ngIf="actions.length === 0 && !newActionDraft()" class="text-xs text-gray-500 italic">
            No actions. Click <b>Add Action</b> to fire SMS/Email when this style triggers.
          </div>
          <table *ngIf="actions.length > 0 || newActionDraft()" class="w-full text-xs">
            <thead>
              <tr class="text-left text-gray-500">
                <th class="px-1 py-1 font-bold">Action</th>
                <th class="px-1 py-1 font-bold">User</th>
                <th class="px-1 py-1 w-16"></th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let a of actions" class="border-t border-gray-100">
                <td class="px-1 py-1.5">{{ actionLabelFor(a.actionName) }}</td>
                <td class="px-1 py-1.5 break-all">{{ actionTargetLabel(a) }}</td>
                <td class="px-1 py-1.5 text-right">
                  <button type="button" (click)="deleteAction.emit(a)" [disabled]="saving" title="Delete action" class="text-red-600 hover:text-red-800 disabled:opacity-40">
                    <lucide-icon [img]="icons.Trash2" [size]="14"></lucide-icon>
                  </button>
                </td>
              </tr>
              <tr *ngIf="newActionDraft() as d" class="border-t border-gray-200 bg-blue-50/60">
                <td class="px-1 py-1.5">
                  <select [ngModel]="d.actionName" (ngModelChange)="updateActionDraft('actionName', $event)" [ngModelOptions]="{standalone: true}" [disabled]="actionSaving" class="bg-white border border-gray-300 rounded text-xs p-1 w-full disabled:opacity-60">
                    <option *ngFor="let a of actionNames" [ngValue]="a.value">{{ a.label }}</option>
                  </select>
                </td>
                <td class="px-1 py-1.5">
                  <select [ngModel]="d.userId" (ngModelChange)="updateActionDraft('userId', $event)" [ngModelOptions]="{standalone: true}" [disabled]="actionSaving || actionUsersLoading" class="bg-white border border-gray-300 rounded text-xs p-1 w-full disabled:opacity-60">
                    <option [ngValue]="null" disabled>
                      {{ actionUsersLoading ? 'Loading users…' : (actionUsers.length ? '— Select user —' : 'No users in this building') }}
                    </option>
                    <option *ngFor="let u of actionUsers" [ngValue]="u.id">{{ actionUserLabel(u, d.actionName) }}</option>
                  </select>
                </td>
                <td class="px-1 py-1.5 text-right">
                  <div class="flex justify-end gap-1">
                    <button type="button" (click)="onSaveAction()" [disabled]="actionSaving" title="Save action" class="text-green-700 hover:text-green-800 disabled:opacity-40">
                      <lucide-icon [img]="icons.Check" [size]="14"></lucide-icon>
                    </button>
                    <button type="button" (click)="cancelAddAction()" [disabled]="actionSaving" title="Cancel" class="text-gray-500 hover:text-gray-700 disabled:opacity-40">
                      <lucide-icon [img]="icons.X" [size]="14"></lucide-icon>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div class="flex justify-end gap-3 px-5 py-3 border-t border-gray-200 bg-white rounded-b-md shrink-0">
      <button type="button" (click)="onClose()" [disabled]="saving" class="px-5 py-2 rounded-md border border-gray-400 oswald-font disabled:opacity-60">Cancel</button>
      <button type="submit" [disabled]="saving || !canSubmit()" class="px-6 py-2 rounded-md bg-blue-500 hover:bg-blue-600 text-white oswald-font disabled:opacity-60 disabled:cursor-not-allowed">
        <ng-container *ngIf="!saving">
          <ng-container *ngIf="mode === 'add'">Create Style</ng-container>
          <ng-container *ngIf="mode === 'edit'">Save</ng-container>
        </ng-container>
        <ng-container *ngIf="saving">
          <ng-container *ngIf="mode === 'add'">Creating…</ng-container>
          <ng-container *ngIf="mode === 'edit'">Saving…</ng-container>
        </ng-container>
      </button>
    </div>
  </form>
</div>
